import { useState, useEffect, useCallback } from 'react';
import { Box, Text, Group, Stack, Button, UnstyledButton } from '@mantine/core';
import GameHeader from '../components/GameHeader';
import PowerupCard from '../components/PowerupCard';
import { POWERUPS } from '../powerups';

function PowerupSelectScreen({
  gameName, roomCode, onReconnect, onQuit,
  myPowerups, players, playerId, currentJudgeId, currentJudgeName,
  currentRound, totalRounds, judgeAnnouncementDuration,
  powerupUsedThisRound, onUsePowerup,
}) {
  const [selectedPowerup, setSelectedPowerup] = useState(null);
  const [targetPlayerId, setTargetPlayerId] = useState(null);
  const [timeLeft, setTimeLeft] = useState(judgeAnnouncementDuration);
  const isFirstDeal = myPowerups.length === 3 && currentRound === 1;

  useEffect(() => {
    setTimeLeft(judgeAnnouncementDuration);
    const interval = setInterval(() => setTimeLeft(t => Math.max(0, t - 1)), 1000);
    return () => clearInterval(interval);
  }, [judgeAnnouncementDuration]);

  // All other players are valid targets (including the judge — they draw too)
  const otherPlayers = players.filter(p => p.id !== playerId);

  const handleCardSelect = useCallback((powerup) => {
    if (powerupUsedThisRound) return;
    setSelectedPowerup(prev => prev?.id === powerup.id ? null : powerup);
    setTargetPlayerId(null);
  }, [powerupUsedThisRound]);

  const handleConfirm = useCallback(() => {
    if (!selectedPowerup) return;
    const def = POWERUPS[selectedPowerup.id];
    if (def.requiresTarget && !targetPlayerId) return;
    onUsePowerup(selectedPowerup.id, targetPlayerId);
    setSelectedPowerup(null);
    setTargetPlayerId(null);
  }, [selectedPowerup, targetPlayerId, onUsePowerup]);

  const needsTarget = selectedPowerup && POWERUPS[selectedPowerup.id]?.requiresTarget;
  const canConfirm = selectedPowerup && (!needsTarget || targetPlayerId);

  return (
    <Box
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 999,
        background: 'linear-gradient(160deg, #0d0d1a 0%, #1a0a2e 50%, #0a1a0d 100%)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <GameHeader
        gameName={gameName}
        roomCode={roomCode}
        onReconnect={onReconnect}
        onQuit={onQuit}
        timerSeconds={timeLeft}
      />

      {/* Judge announcement */}
      <Box
        style={{
          flexShrink: 0,
          padding: '0.5rem 1rem',
          textAlign: 'center',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <Text size="xs" style={{ color: '#888', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
          Round {currentRound} of {totalRounds}
        </Text>
        <Text fw={800} style={{ color: '#FFD700', fontSize: '1.2rem', textShadow: '0 0 20px rgba(255,215,0,0.4)' }}>
          {currentJudgeName} is the Judge
        </Text>
      </Box>

      <Box style={{ flex: 1, overflowY: 'auto', padding: '1rem' }}>
        <Stack gap="md" align="center">

          {myPowerups.length > 0 ? (
            <>
              <Text size="sm" fw={600} style={{ color: powerupUsedThisRound ? '#888' : '#bbb' }}>
                {powerupUsedThisRound ? 'Powerup played! Drawing starts soon...' : 'Tap a card to play a powerup'}
              </Text>

              {/* Card hand */}
              <Group gap="sm" justify="center" wrap="nowrap">
                {myPowerups.map((powerup, i) => (
                  <PowerupCard
                    key={`${powerup.id}-${i}`}
                    powerup={powerup}
                    selected={selectedPowerup?.id === powerup.id}
                    onSelect={handleCardSelect}
                    disabled={powerupUsedThisRound}
                    dealDelay={isFirstDeal ? i * 500 : 0}
                    forceRevealed={!isFirstDeal}
                  />
                ))}
              </Group>

              {/* Powerup action panel */}
              {selectedPowerup && !powerupUsedThisRound && (
                <Box
                  style={{
                    width: '100%',
                    maxWidth: 380,
                    background: 'rgba(255,255,255,0.06)',
                    border: `2px solid ${selectedPowerup.color}44`,
                    borderRadius: 14,
                    overflow: 'hidden',
                  }}
                >
                  {/* Powerup header */}
                  <Box
                    style={{
                      padding: '0.75rem 1rem',
                      background: `${selectedPowerup.color}22`,
                      borderBottom: `1px solid ${selectedPowerup.color}33`,
                      textAlign: 'center',
                    }}
                  >
                    <Group justify="center" gap="xs">
                      <i className={`fa-solid ${selectedPowerup.icon}`} style={{ color: selectedPowerup.color, fontSize: '1rem' }} />
                      <Text fw={700} style={{ color: selectedPowerup.color }}>
                        {selectedPowerup.name}
                      </Text>
                    </Group>
                    <Text size="xs" style={{ color: '#bbb', marginTop: 2 }}>
                      {selectedPowerup.description}
                    </Text>
                  </Box>

                  {/* Target selection */}
                  {needsTarget ? (
                    <Box style={{ padding: '0.75rem 1rem' }}>
                      <Text
                        size="xs"
                        fw={700}
                        style={{
                          color: '#aaa',
                          textTransform: 'uppercase',
                          letterSpacing: '0.08em',
                          textAlign: 'center',
                          marginBottom: '0.5rem',
                        }}
                      >
                        Choose your target
                      </Text>
                      <Stack gap="xs">
                        {otherPlayers.map(player => {
                          const isSelected = targetPlayerId === player.id;
                          const isJudge = player.id === currentJudgeId;
                          return (
                            <UnstyledButton
                              key={player.id}
                              onClick={() => setTargetPlayerId(player.id)}
                              style={{
                                width: '100%',
                                padding: '0.6rem 1rem',
                                borderRadius: 8,
                                border: isSelected
                                  ? `2px solid ${selectedPowerup.color}`
                                  : '2px solid rgba(255,255,255,0.12)',
                                background: isSelected
                                  ? `${selectedPowerup.color}22`
                                  : 'rgba(255,255,255,0.04)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                transition: 'all 0.15s',
                              }}
                            >
                              <Text fw={700} style={{ color: isSelected ? selectedPowerup.color : '#ddd', fontSize: '1rem' }}>
                                {player.name}
                              </Text>
                              <Group gap="xs" align="center">
                                {isJudge && (
                                  <Text size="xs" style={{ color: '#FFD700', fontWeight: 700 }}>
                                    JUDGE
                                  </Text>
                                )}
                                {isSelected && (
                                  <i className="fa-solid fa-check" style={{ color: selectedPowerup.color }} />
                                )}
                              </Group>
                            </UnstyledButton>
                          );
                        })}
                      </Stack>
                    </Box>
                  ) : (
                    <Box style={{ padding: '0.5rem 1rem 0' }} />
                  )}

                  {/* Confirm button */}
                  <Box style={{ padding: '0 1rem 1rem' }}>
                    <Button
                      fullWidth
                      onClick={handleConfirm}
                      disabled={!canConfirm}
                      style={{
                        background: canConfirm
                          ? `linear-gradient(135deg, ${selectedPowerup.color}, ${selectedPowerup.color}bb)`
                          : undefined,
                        fontWeight: 800,
                        letterSpacing: '0.05em',
                        height: 44,
                      }}
                    >
                      {needsTarget && !targetPlayerId ? 'Pick a target above' : 'PLAY IT'}
                    </Button>
                  </Box>
                </Box>
              )}

              {!powerupUsedThisRound && (
                <Button
                  variant="subtle"
                  color="gray"
                  size="xs"
                  onClick={() => setSelectedPowerup(null)}
                  style={{ color: '#555' }}
                >
                  Skip — save powerups for later
                </Button>
              )}
            </>
          ) : (
            <Box ta="center" style={{ paddingTop: '2rem' }}>
              <i className="fa-solid fa-hand" style={{ fontSize: '3rem', color: '#444' }} />
              <Text size="sm" style={{ color: '#666', marginTop: 8 }}>No powerups left</Text>
              <Text size="xs" style={{ color: '#555', marginTop: 4 }}>Drawing starts soon...</Text>
            </Box>
          )}
        </Stack>
      </Box>
    </Box>
  );
}

export default PowerupSelectScreen;
